import { FC } from 'react';
import { Controller } from 'react-hook-form';
import { Grid, Stack, TextField, Typography } from '@mui/material';
import { useDictionary } from '@cvt/hooks/useDictionary';
import { StoryTypeSelect } from '@modules/Stories/components/StoryTypeSelect';
import { PreferenceSelect } from '@modules/Stories/components/PreferenceSelect';
import { StoryTagAutocomplete } from '@modules/Stories/components/StoryTagAutocomplete';
import { StatusSelect } from '@modules/Stories/components/StatusSelect';


type Props = {
  defaultValues?: Partial<Stories.Create>;
};

export const MetadataFields: FC<Props> = (props) => {
  const { defaultValues } = props;
  const dictionary = useDictionary();

  return (
    <Stack gap={3}>
      <Typography variant="body1" fontSize={30} fontWeight={600}>
        Story Metadata
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Configure your story's basic information
      </Typography>

      <Grid container gap={2}>
        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Story Title</Typography>
          <Controller
            name="title"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                variant="outlined"
                color="primary"
                fullWidth
                multiline
                minRows={1}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                placeholder={dictionary.forms.story.themePlaceholder}
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <Grid container size={12} spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" fontWeight={500} mb={1}>Story Type</Typography>
              <Controller
                name="type"
                rules={{
                  required: dictionary.forms.validations.required,
                }}
                render={({ field: { ref, ...field }, fieldState }) => (
                  <StoryTypeSelect
                    {...field}
                    inputRef={ref}
                    value={field.value || ''}
                    fullWidth
                    label=""
                    onChange={(event) => {
                      const selectedValue = event.target.value;
                      field.onChange(selectedValue);
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12,  md: 6 }}>
              <Typography variant="body2" fontWeight={500} mb={1}>Status</Typography>
              <Controller
                name="status"
                render={({ field }) => (
                  <StatusSelect fullWidth showEmpty={false} {...field}/>
                )}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Linked Objectives</Typography>
          <Controller
            name="objectives"
            render={({ field, fieldState }) => (
              <PreferenceSelect
                {...field}
                value={field.value || ''}
                defaultValue={defaultValues?.objectives || []}
                field="objectives"
                fullWidth
                multiple
                label=""
                onChange={(event) => {
                  const selectedValue = event.target.value;
                  field.onChange(selectedValue);
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Linked Values</Typography>
          <Controller
            name="values"
            render={({ field, fieldState }) => (
              <PreferenceSelect
                {...field}
                value={field.value || ''}
                field="values"
                fullWidth
                defaultValue={defaultValues?.values || []}
                multiple
                label=""
                onChange={(event) => {
                  const selectedValue = event.target.value;
                  field.onChange(selectedValue);
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>

        <Grid size={12}>
          <Typography variant="body2" fontWeight={500} mb={1}>Story Tags</Typography>
          <Controller
            name="tags"
            render={({ field, fieldState }) => (
              <StoryTagAutocomplete
                {...field}
                freeSolo
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};
